import { useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { spacing } from '@tabbio/tokens';
import { Text } from '../common/AppText';
import { NativeButton } from '../common/NativeButton';
import { useTheme } from '../../hooks/useTheme';
import { cvEditionFields } from '../../lib/cv-edition-edit';

/** A bounded, readable disclosure of the selected document. Source records,
 * permissions and internal identifiers never appear in this preview. */
export function CvSelectedEditionPreview({ document, pending, unavailable }: {
  document?: Record<string, unknown>; pending?: boolean; unavailable?: boolean;
}) {
  const { t } = useTranslation('seeker');
  const { theme } = useTheme();
  const [expanded, setExpanded] = useState(false);
  if (pending) return <ActivityIndicator accessibilityLabel={t('cv.review.loading')} />;
  if (unavailable) return <Text style={{ color: theme.textSecondary }}>{t('cv.review.unavailable')}</Text>;
  if (!document) return null;
  const direction = document.direction === 'rtl' ? 'rtl' : 'ltr';
  const socials = document.socials && typeof document.socials === 'object' ? document.socials as Record<string, unknown> : {};
  const fields = cvEditionFields(document);
  return <View style={{ gap: spacing[2] }}>
    <NativeButton title={t(expanded ? 'cv.review.hideSelectedPreview' : 'cv.review.showSelectedPreview')} variant="outline" size="sm"
      accessibilityState={{ expanded }} onPress={() => setExpanded((value) => !value)} />
    {expanded ? <View style={{ gap: spacing[2], padding: spacing[3], borderWidth: 1, borderColor: theme.border, borderRadius: 12 }}>
      {fields.map((field) => <View key={field.path.join('.')}>
        <Text style={{ color: theme.textSecondary }}>{field.section ? `${t(`cv.review.sections.${field.section}`)} ${field.item ?? ''} · ` : ''}{t(`cv.review.fields.${field.label}`)}</Text>
        <Text style={{ color: theme.text, writingDirection: direction }}>{field.value || t('cv.review.blank')}</Text>
      </View>)}
      {['email', 'phone', 'whatsapp', 'linkedin', 'website', 'github', 'twitter', 'instagram', 'facebook', 'youtube', 'tiktok'].map((key) => typeof socials[key] === 'string'
        ? <Text key={key} style={{ color: theme.text, writingDirection: 'auto' }}>{key}: {socials[key] as string}</Text> : null)}
      {typeof document.avatarUrl === 'string' ? <Text style={{ color: theme.text }}>{t('cv.review.fields.photoIncluded')}</Text> : null}
    </View> : null}
  </View>;
}
